import React from 'react';
import PropTypes from 'prop-types';
import {Empty} from './Empty';
import {Wrapper} from './Wrapper';
import {Feedback} from '../Feedback';
import {HexagonNode} from '@learningblocks/storybook-components';
import {Utilities} from '@learningblocks/exp-constants';
const {
  COMPONENT_TYPE,
  DRAG_DROP_DATA,
  DRAG_DROP__ID,
  DRAG_DROP__TYPE,
} = Utilities;

import './style.scss';

export function Outcome(props) {
  const onSelect = () => props.onSelect && props.onSelect();

  const onRemove = () => !props.isCorrect && props.onRemove && props.onRemove();

  const withoutFeedback = () => (
    <Wrapper className='design-view__outcomeComp'>
      <HexagonNode
        tabIndex={props.tabIndex}
        title={props.title}
        image={props.image}
        glossaryId={props.glossaryId}
      />
    </Wrapper>
  );

  const option = () => (
    <Wrapper className='design-view__outcomeCompOption' ariaLabel={`${props.title}. Draggable node.`}>
      <HexagonNode
        title={props.title}
        image={props.image}
        addCard={props.onSelect}
        onSelect={onSelect}
        className='design-view__outcomeCompOption--item'
        glossaryId={props.glossaryId}
      />
    </Wrapper>
  );

  const mobileViewOption = () => (
    <Wrapper className='design-view__outcomeCompOption'>
      <HexagonNode
        tabIndex={props.tabIndex}
        title={props.title}
        image={props.image}
        onSelect={onSelect}
        className='design-view__outcomeCompOption--item'
        glossaryId={props.glossaryId}
      />
    </Wrapper>
  );

  const stepCorrect = () => (
    <Wrapper className='design-view__outcomeComp'>
      <HexagonNode
        tabIndex={props.tabIndex}
        title={props.title}
        image={props.image}
        glossaryId={props.glossaryId}
      />
      <Feedback.Wrap
        onSelect={onRemove}
        tabIndex={-1}
        className='design-view__outcomeComp_feedback'>
        <Feedback.IconCorrect className='design-view__outcomeComp_feedback--icon' />
      </Feedback.Wrap>
    </Wrapper>
  );

  const stepIncorrect = () => (
    <Wrapper className='design-view__outcomeComp'>
      <HexagonNode
        tabIndex={props.tabIndex}
        title={props.title}
        image={props.image}
        glossaryId={props.glossaryId}
      />
      <Feedback.Wrap
        onSelect={onRemove}
        tabIndex={props.tabIndex}
        className='design-view__outcomeComp_feedback'>
        <Feedback.IconIncorrect
          show={props.showTooltip}
          className='design-view__outcomeComp_feedback--icon'
          message={props.feedbackMessage}
        />
      </Feedback.Wrap>
    </Wrapper>
  );

  const onDrop = React.useCallback((event) => {
    const dropData = JSON.parse(event.dataTransfer.getData(DRAG_DROP_DATA));
    const optionId = dropData[DRAG_DROP__ID];
    const componentType = dropData[DRAG_DROP__TYPE];
    props.onDrop({optionId, componentType});
  }, []);

  const onAddStep = (params) => {
    props.onDrop({...params, componentType: COMPONENT_TYPE.OUTCOME_CARD});
  };

  const emptyStep = () => (
    <Wrapper
      className='design-view__outcomeComp'
      onDrop={onDrop}>
      <Empty
        text={props.placeHolder}
        options={props.optionsList}
        onAddStep={onAddStep}
      />
    </Wrapper>
  );

  if (!props.showFeedback) {
    return withoutFeedback();
  } else if (props.onSelect) {
    return option();
  } else {
    if (props.isEmpty) {
      return emptyStep();
    } else {
      if (props.isCorrect) {
        return stepCorrect();
      } else if (props.isCorrect === false) {
        return stepIncorrect();
      } else {
        return mobileViewOption();
      }
    }
  }
}

Outcome.propTypes = {
  glossaryId: PropTypes.string,
  /**
   * tabIndex to set element and children
   */
  tabIndex: PropTypes.number,
  /**
   * Title to display on Outcome
   */
  title: PropTypes.string,
  /**
   * Url to image
   */
  image: PropTypes.string,
  /**
   * Placeholder text when no Node is dropped
   */
  placeHolder: PropTypes.string,
  /**
   * Feedback message
   */
  feedbackMessage: PropTypes.string,
  /**
   * Is the Option placed correct or incorrect
   */
  isCorrect: PropTypes.bool,
  /**
   * Function to trigger remove option from step action when wrongly dropped
   * Triggered on click of Incorrect-Icon
   */
  onRemove: PropTypes.func,
  /**
   * Function to trigger add as next step action ( when used as option )
   * Triggered on click of MoveIcon
   */
  onSelect: PropTypes.func,
  /**
   * Boolean  to override feedback icon display
   */
  showFeedback: PropTypes.bool,
  /**
   * Is the Step Empty
   */
  isEmpty: PropTypes.bool.isRequired,
  /**
   * Function involked when an option is dropped
   * on EmptyNodeHolder
   */
  onDrop: PropTypes.func,
  /**
   * for keyboard navigation
   */
  tabIndex: PropTypes.number,
};

Outcome.defaultProps = {
  onRemove: undefined,
  onSelect: undefined,
  showFeedback: true,
};
